import { Link, useParams, useSearchParams } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { Panel } from '../../components/Panel'
import { Metric } from '../../components/Metric'
import { LineChart } from '../../components/charts/LineChart'
import { BarList } from '../../components/charts/BarList'
import { visitorsApi, type Downloader } from '../../api/visitors'
import { fmtDate, fmtInt, fmtRelative, PUBLICATION_NAMES } from '../../lib/format'
import { rate } from '../../lib/visitorsFormat'
import { useVisitorsRange } from '../../lib/useVisitorsRange'
import { useMemberAccess } from './engagementBits'
import { QueryProblem, Skeleton, VisitorsFrame, linkCls, tdn, th, thr } from './VisitorsFrame'

/** Issue downloads: the PDF button in the issue viewer, counted on our server. */
export function VisitorsDownloads() {
  const { pub = '' } = useParams()
  const { range, search } = useVisitorsRange()
  const [params, setParams] = useSearchParams()
  const okPub = Object.hasOwn(PUBLICATION_NAMES, pub)
  const issueParam = params.get('issue')
  const issueId = issueParam && /^[0-9]{1,18}$/.test(issueParam) ? Number(issueParam) : null
  const { canMember } = useMemberAccess()
  const q = useQuery({
    queryKey: ['visitors-downloads', pub, range.from, range.to, issueId],
    queryFn: () => visitorsApi.downloads(pub, range, issueId),
    enabled: okPub,
  })
  const base = `/visitors/${pub}`
  const d = q.data
  const titleOf = (id: number) => d?.issues.find((i) => i.issue_id === id)?.title ?? `Issue #${id}`
  const pick = (id: number | null) => {
    const next = new URLSearchParams(params)
    if (id === null) next.delete('issue')
    else next.set('issue', String(id))
    setParams(next, { replace: true })
  }
  const who = (p: Downloader) => {
    if (p.subscriber_id !== null) {
      const label = p.name ?? `#${p.subscriber_id}`
      return (
        <>
          <Link to={`${base}/members/${p.subscriber_id}?${search}`} className={linkCls}>{label}</Link>
          {p.account_number && <span className="text-paper-faint"> · {p.account_number}</span>}
        </>
      )
    }
    if (p.visitor_id) {
      return (
        <>
          <Link to={`${base}/visitors/${encodeURIComponent(p.visitor_id)}?${search}`} className={linkCls}>{p.visitor_id}</Link>
          {p.member && <span className="text-paper-faint"> · member</span>}
        </>
      )
    }
    return <span className="text-paper-faint">—</span>
  }

  return (
    <VisitorsFrame pub={pub} valid={okPub} title="Issue downloads" crumbs={[{ label: 'Issue downloads' }]}>
      {q.isError ? (
        <QueryProblem error={q.error} pub={pub} />
      ) : !d ? (
        <Skeleton />
      ) : (
        <>
          <Panel label="Downloads" accent="amber">
            <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
              <Metric size="sm" label="Downloads" value={d.downloads} sub="PDF button clicks, repeats within 30 min counted once" />
              <Metric size="sm" label="Downloaders" value={d.downloaders}
                      sub={d.private_downloads ? `+ ${fmtInt(d.private_downloads)} with the privacy signal (not identified)` : 'unique members / visitors'} />
              <Metric size="sm" label="Issues downloaded" value={d.issues_downloaded} />
              <Metric size="sm" label="Opens in viewer" value={d.opens} sub="same days, people only" />
              <Metric size="sm" label="Downloads per open" value={d.opens ? (d.downloads / d.opens).toFixed(2) : '—'} sub="downloads ÷ viewer opens" />
            </div>
            <p className="mt-3 font-mono text-[11px] text-paper-faint">
              Counted since download tracking started (October 2026); there is no earlier history. Every download needs a signed-in, entitled member, so none is filtered as a bot.
            </p>
          </Panel>

          <Panel label="Downloads per day" accent="cyan">
            <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,2fr)_minmax(0,1fr)] gap-6">
              <LineChart
                ariaLabel="Issue downloads per day"
                labels={d.by_day.map((x) => x.day.slice(5))}
                series={[{ label: 'Downloads', values: d.by_day.map((x) => x.downloads), tone: 'amber' }]}
              />
              <div className="space-y-6">
                <div>
                  <h3 className="label mb-2">By device</h3>
                  {d.by_device.length ? <BarList rows={d.by_device.map((x) => ({ label: x.key, value: x.downloads }))} />
                    : <p className="font-mono text-[12px] text-paper-faint">—</p>}
                </div>
                <div>
                  <h3 className="label mb-2">By channel of the visit</h3>
                  {d.by_channel.length ? <BarList rows={d.by_channel.map((x) => ({ label: x.key, value: x.downloads }))} />
                    : <p className="font-mono text-[12px] text-paper-faint">—</p>}
                </div>
              </div>
            </div>
          </Panel>

          <Panel label="By issue" accent="violet">
            <div className="overflow-x-auto">
              <table className="w-full font-mono text-[12px] text-paper">
                <thead>
                  <tr className="border-b border-ink-500">
                    <th className={th}>Issue</th>
                    {['Downloads', 'Downloaders', 'Viewer opens', 'Readers', 'Readers who downloaded'].map((h) => <th key={h} className={thr}>{h}</th>)}
                  </tr>
                </thead>
                <tbody>
                  {d.issues.map((i) => (
                    <tr key={i.issue_id} className={`border-b border-ink-500/50 ${issueId === i.issue_id ? 'bg-ink-700' : ''}`}>
                      <td className="px-3 py-2">
                        <div className="flex items-center gap-3">
                          {i.cover_image_url && <img src={i.cover_image_url} alt="" className="w-8 h-10 object-cover border border-ink-500" loading="lazy" />}
                          <button type="button" onClick={() => pick(i.issue_id)} className={`${linkCls} text-left`}
                                  aria-pressed={issueId === i.issue_id}>
                            {i.title}
                          </button>
                          {i.post_date && <span className="text-paper-faint">{fmtDate(i.post_date)}</span>}
                        </div>
                      </td>
                      <td className={tdn}>{fmtInt(i.downloads)}</td>
                      <td className={tdn}>{fmtInt(i.downloaders)}</td>
                      <td className={tdn}>{fmtInt(i.opens)}</td>
                      <td className={tdn}>{fmtInt(i.readers)}</td>
                      <td className={tdn}>{i.download_share === null ? '—' : rate(Math.min(1, i.download_share))}</td>
                    </tr>
                  ))}
                  {d.issues.length === 0 && <tr><td colSpan={6} className="px-3 py-4 text-paper-faint">No downloads or opens in this range</td></tr>}
                </tbody>
              </table>
            </div>
          </Panel>

          <Panel
            label={issueId ? `Who downloaded ${titleOf(issueId)}` : 'Who downloaded'}
            accent="green"
            right={issueId ? <button type="button" onClick={() => pick(null)} className={`${linkCls} font-mono text-[12px]`}>All issues</button> : undefined}
          >
            {!canMember && (
              <p className="mb-3 font-mono text-[11px] text-paper-faint">
                Names need “Can view member reading history”; you see visitor ids instead.
              </p>
            )}
            <div className="overflow-x-auto">
              <table className="w-full font-mono text-[12px] text-paper">
                <thead>
                  <tr className="border-b border-ink-500">
                    <th className={th}>Who</th>
                    <th className={thr}>Downloads</th>
                    <th className={th}>Issues</th>
                    <th className={thr}>Last</th>
                  </tr>
                </thead>
                <tbody>
                  {d.people.map((p, n) => (
                    <tr key={`${p.subscriber_id ?? ''}-${p.visitor_id ?? ''}-${n}`} className="border-b border-ink-500/50">
                      <td className="px-3 py-2">{who(p)}</td>
                      <td className={tdn}>{fmtInt(p.downloads)}</td>
                      <td className="px-3 py-2 text-paper-dim">{p.issues.map(titleOf).join(', ')}</td>
                      <td className={tdn} title={p.last_at}>{fmtRelative(p.last_at)}</td>
                    </tr>
                  ))}
                  {d.people.length === 0 && <tr><td colSpan={4} className="px-3 py-4 text-paper-faint">No one identified</td></tr>}
                </tbody>
              </table>
            </div>
            {d.people_truncated && <p className="mt-2 font-mono text-[11px] text-paper-faint">Showing the 500 most recent.</p>}
          </Panel>
        </>
      )}
    </VisitorsFrame>
  )
}
